import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db } from '../config/firebase';
import { collection, onSnapshot, query, orderBy, addDoc, updateDoc, doc, serverTimestamp, deleteDoc } from 'firebase/firestore';

function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '', description: '', status: 'Pending', priority: 'Medium',
    assignedTo: '', assignedEmail: '', deadline: '', progress: 0, tags: '', notes: ''
  });

  useEffect(() => {
    const q = query(collection(db, 'tasks'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setTasks(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoading(false);
    });
    return () => unsub();
  }, []);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const tagsArray = newTask.tags.split(',').map(t => t.trim()).filter(Boolean);
      await addDoc(collection(db, 'tasks'), {
        title: newTask.title, description: newTask.description,
        status: newTask.status, priority: newTask.priority,
        assignedTo: newTask.assignedTo, assignedEmail: newTask.assignedEmail,
        deadline: newTask.deadline, progress: parseInt(newTask.progress) || 0,
        tags: tagsArray, notes: newTask.notes, createdAt: serverTimestamp()
      });
      setIsCreating(false);
      setNewTask({ title: '', description: '', status: 'Pending', priority: 'Medium', assignedTo: '', assignedEmail: '', deadline: '', progress: 0, tags: '', notes: '' });
    } catch (err) { console.error('Create failed:', err); alert('Failed to create task'); }
  };

  const handleUpdateStatus = async (task, newStatus) => {
    try {
      await updateDoc(doc(db, 'tasks', task.id), { status: newStatus, progress: newStatus === 'Completed' ? 100 : task.progress });
    } catch (err) { console.error('Update failed:', err); }
  };

  const handleDelete = async (taskId) => {
    if (window.confirm('Delete this task?')) {
      await deleteDoc(doc(db, 'tasks', taskId));
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = (task.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (task.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || (task.status?.toLowerCase().replace(' ', '-') === filterStatus);
    const matchesPriority = filterPriority === 'all' || (task.priority?.toLowerCase() === filterPriority);
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in progress': return 'bg-blue-100 text-blue-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch ((priority || '').toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-blue-100 text-blue-700';
      case 'low': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex-1 overflow-y-auto bg-gray-50 transition-all duration-300" style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}>
        <div className="p-6 min-h-screen">
          <div className="mb-6 flex justify-between items-center">
            <div>
              <div className="flex items-center">
                {isMobile && (
                  <button onClick={toggleMobileSidebar} className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                  </button>
                )}
                <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
              </div>
              <p className="text-sm text-gray-600 mt-1">Track and manage all tasks</p>
            </div>
            <button onClick={() => setIsCreating(true)} className="px-4 py-2 bg-brand-golden text-white rounded-lg hover:bg-yellow-600 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
              Create Task
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 shadow">
              <p className="text-xs text-gray-500 font-medium">Total Tasks</p>
              <h3 className="text-xl font-bold text-gray-900 mt-1">{tasks.length}</h3>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <p className="text-xs text-gray-500 font-medium">In Progress</p>
              <h3 className="text-xl font-bold text-blue-600 mt-1">{tasks.filter(t => t.status === 'In Progress').length}</h3>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <p className="text-xs text-gray-500 font-medium">Completed</p>
              <h3 className="text-xl font-bold text-green-600 mt-1">{tasks.filter(t => t.status === 'Completed').length}</h3>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <p className="text-xs text-gray-500 font-medium">Pending</p>
              <h3 className="text-xl font-bold text-yellow-600 mt-1">{tasks.filter(t => t.status === 'Pending').length}</h3>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <input type="text" placeholder="Search tasks..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </div>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg">
                <option value="all">All Status</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
              </select>
              <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg">
                <option value="all">All Priority</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Task</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Assigned To</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Progress</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Deadline</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTasks.map((task) => (
                    <tr key={task.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{task.title}</p>
                        <p className="text-xs text-gray-500 truncate max-w-xs">{task.description}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                            {(task.assignedTo || 'NA').split(' ').map(n => n[0]).join('')}
                          </div>
                          <span className="text-sm text-gray-900">{task.assignedTo || 'Unassigned'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-medium ${getPriorityColor(task.priority)}`}>{task.priority}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[60px]">
                            <div className={`h-2 rounded-full ${(task.progress || 0) === 100 ? 'bg-green-500' : (task.progress || 0) >= 50 ? 'bg-blue-500' : 'bg-yellow-500'}`} style={{ width: `${task.progress || 0}%` }}></div>
                          </div>
                          <span className="text-xs font-medium text-gray-900">{task.progress || 0}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="text-sm text-gray-600">{task.deadline}</span></td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(task.status)}`}>{task.status}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => setSelectedTask(task)} className="text-xs text-blue-600 hover:text-blue-700 font-medium">View</button>
                          <button onClick={() => handleDelete(task.id)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {filteredTasks.length === 0 && !isLoading && <div className="text-center py-8 bg-white rounded-lg shadow"><p className="text-gray-500">No tasks found</p></div>}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTask(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-5 text-white relative">
              <button onClick={() => setSelectedTask(null)} className="absolute top-3 right-3 text-white hover:bg-white/20 rounded-full p-1.5">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              <h2 className="text-xl font-bold mb-1">{selectedTask.title}</h2>
              <p className="text-sm text-blue-100">{selectedTask.description}</p>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex gap-3">
                <div className="flex-1"><h3 className="text-xs font-medium text-gray-500 mb-2">Status</h3><span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTask.status)}`}>{selectedTask.status}</span></div>
                <div className="flex-1"><h3 className="text-xs font-medium text-gray-500 mb-2">Priority</h3><span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedTask.priority)}`}>{selectedTask.priority}</span></div>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">Progress</h3>
                <div className="flex items-center space-x-3">
                  <div className="flex-1 bg-gray-200 rounded-full h-3"><div className={`h-3 rounded-full ${(selectedTask.progress || 0) === 100 ? 'bg-green-500' : (selectedTask.progress || 0) >= 50 ? 'bg-blue-500' : 'bg-yellow-500'}`} style={{ width: `${selectedTask.progress || 0}%` }}></div></div>
                  <span className="text-sm font-bold text-gray-900">{selectedTask.progress || 0}%</span>
                </div>
              </div>
              {selectedTask.tags?.length > 0 && (
                <div><h3 className="text-sm font-bold text-gray-900 mb-2">Tags</h3><div className="flex flex-wrap gap-2">{selectedTask.tags.map((tag, idx) => <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md font-medium">#{tag}</span>)}</div></div>
              )}
              {selectedTask.notes && <div><h3 className="text-sm font-bold text-gray-900 mb-2">Notes</h3><div className="p-3 bg-gray-50 rounded-lg"><p className="text-sm text-gray-700">{selectedTask.notes}</p></div></div>}
              <div className="flex gap-2 pt-3">
                {selectedTask.status !== 'Completed' && <button onClick={() => handleUpdateStatus(selectedTask, 'Completed')} className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-3 rounded-lg">Mark Complete</button>}
                {selectedTask.status !== 'In Progress' && selectedTask.status !== 'Completed' && <button onClick={() => handleUpdateStatus(selectedTask, 'In Progress')} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-3 rounded-lg">Start Work</button>}
                <button onClick={() => { setSelectedTask(null); handleDelete(selectedTask.id); }} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-3 rounded-lg">Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {isCreating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setIsCreating(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Task</h2>
            <form onSubmit={handleCreateTask} className="space-y-3">
              <input type="text" required placeholder="Task title" value={newTask.title} onChange={e => setNewTask({...newTask, title: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <textarea placeholder="Description" value={newTask.description} onChange={e => setNewTask({...newTask, description: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              <div className="grid grid-cols-2 gap-3">
                <input type="text" placeholder="Assigned to (name)" value={newTask.assignedTo} onChange={e => setNewTask({...newTask, assignedTo: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <input type="email" placeholder="Email" value={newTask.assignedEmail} onChange={e => setNewTask({...newTask, assignedEmail: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <select value={newTask.priority} onChange={e => setNewTask({...newTask, priority: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg text-sm"><option>Low</option><option>Medium</option><option>High</option><option>Critical</option></select>
                <select value={newTask.status} onChange={e => setNewTask({...newTask, status: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg text-sm"><option>Pending</option><option>In Progress</option><option>Completed</option></select>
                <input type="date" value={newTask.deadline} onChange={e => setNewTask({...newTask, deadline: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <input type="number" min="0" max="100" placeholder="Progress %" value={newTask.progress} onChange={e => setNewTask({...newTask, progress: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <input type="text" placeholder="Tags (comma separated)" value={newTask.tags} onChange={e => setNewTask({...newTask, tags: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <textarea placeholder="Notes" value={newTask.notes} onChange={e => setNewTask({...newTask, notes: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setIsCreating(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-brand-golden text-white rounded-lg hover:bg-yellow-600">Create Task</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tasks;
